/**
 * leadSourceOptions.ts
 *
 * Single source of truth for the Lead Source / Referral Source filter: its
 * option shape, its option builder, and its FilterConfig.
 *
 * Every list page that filters on lead source MUST build its options with
 * `buildLeadSourceOptions()` (or read them from `/filters/options`, which the
 * API shapes identically) and declare the filter with
 * `leadSourceFilterConfig()`. Before this module the Plans and Leads lists each
 * had their own option shape and their own server-side filter engine, so the
 * two lists behaved differently for the same selection.
 *
 * Option shape — a two-level hierarchy flattened onto `FilterOption.group`:
 *
 *   { value: 'filtersgroup-4', label: 'Digital (category)', group: 'Digital' }
 *   { value: '17',             label: 'Google Ads',         group: 'Digital' }
 *   { value: '18',             label: 'Facebook',           group: 'Digital' }
 *
 * `FilterChecklist` renders that as a collapsible tree (see `collapsibleGroups`
 * in types/filters.types.ts) where the category row is a real, selectable
 * option — not a client-side expansion of its children.
 *
 * The category is an INDEPENDENT selection, not "all of its sources": a record
 * stores the category and the source in separate columns and may carry either
 * without the other. Plans written by the Next Connect signup carry a category
 * with no source (AuthController), and lead→patient conversion writes a source
 * and drops the category (LeadsController) — so neither is a subset of the
 * other, and the label must not read as "all children". Categories with no
 * sources at all are legal and remain selectable.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterConfig, FilterOption } from '@/types/filters.types';
import * as adminSvc from '@/services/admin.service';
import type { LeadSourceCategory } from '@/services/admin.service';

/**
 * Sentinel prefix marking a lead-source *category* (`referral_level1`) option
 * value, as opposed to an individual lead source (`referral_level2`). The two
 * tables have independent autoincrement keys, so without the prefix the server
 * cannot tell which column an id belongs to.
 *
 * Recognized server-side by `FilterNormalizer::fromMarketingFormat()`
 * ('lead-source' case) and `LeadCentralFilters::applyLeadSource()`, which route
 * the id to the category column (`plans.referralGeneral` /
 * `referral_level2.rootID`) instead of the specific column. That column is the
 * only way to reach a record tagged with a category but no source, which is
 * why the category cannot be expanded into member ids client-side.
 *
 * DO NOT change this string — saved dynamic lists store these values forever.
 * `g-<id>` is an older variant the API still accepts on read; never emit it.
 */
export const LEAD_SOURCE_GROUP_PREFIX = 'filtersgroup-';

/** True when a lead-source filter value refers to a whole category. */
export function isLeadSourceGroupValue(value: string): boolean {
  return value.startsWith(LEAD_SOURCE_GROUP_PREFIX);
}

/** The API returns `active` as a bool, `'1'`, or `1` depending on the route. */
function isActive(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

/**
 * Flatten `/lead-source-categories` into the canonical option list above.
 * Inactive categories are dropped along with all their sources; inactive
 * sources are dropped individually. A category with no active sources still
 * yields its own option — records can be tagged with just the category.
 * Category and source order is whatever the API returns (admin-controlled
 * `rl1Order` / `rl2Order`).
 */
export function buildLeadSourceOptions(
  categories: LeadSourceCategory[] | undefined,
): FilterOption[] {
  return (categories ?? []).flatMap((cat) => {
    if (!isActive(cat.active)) return [];
    const group = cat.name || 'Uncategorized';
    const items: FilterOption[] = [
      {
        value: `${LEAD_SOURCE_GROUP_PREFIX}${cat.referralLevel1ID}`,
        label: `${group} (category)`,
        group,
      },
    ];
    for (const src of cat.leadSources ?? []) {
      if (!isActive(src.active)) continue;
      items.push({
        value: String(src.referralLevel2ID),
        label: src.name || 'Unnamed Source',
        group,
      });
    }
    return items;
  });
}

/**
 * Shared query for the lead-source hierarchy. The query key matches the one
 * used by `usePlanFilterOptions` / `usePatientFilterOptions` so every consumer
 * hits the same React Query cache entry.
 */
export function useLeadSourceCategories(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['lead-source-categories'],
    queryFn: adminSvc.listLeadSourceCategories,
    staleTime: 5 * 60_000,
    enabled: opts?.enabled ?? true,
  });
}

/** Canonical lead-source options, ready to hand to `FilterSidebar`. */
export function useLeadSourceOptions(opts?: { enabled?: boolean }): FilterOption[] {
  const { data } = useLeadSourceCategories(opts);
  return useMemo(() => buildLeadSourceOptions(data), [data]);
}

/**
 * The same options shaped for a `SearchableMultiSelect`, for a single picker
 * outside a filter sidebar. A source carries its category as the subtitle,
 * since a source's name alone does not say which category it belongs to.
 */
export function useLeadSourcePickerOptions(opts?: {
  enabled?: boolean;
}): { value: string; label: string; subtitle?: string }[] {
  const options = useLeadSourceOptions(opts);
  return useMemo(
    () =>
      options.map((o) => ({
        value: o.value,
        label: o.label,
        subtitle: isLeadSourceGroupValue(o.value) ? undefined : o.group,
      })),
    [options],
  );
}

/**
 * Overlay the canonical lead-source options onto an options map loaded from
 * `GET /filters/options`.
 *
 * Pages that read every filter's options from that one bulk endpoint (Leads,
 * CRM, CRM dashboard) would otherwise depend on the API emitting the grouped
 * shape. Building the hierarchy client-side from `/lead-source-categories` —
 * exactly what the Plans and Patients lists do — makes the tree work off an
 * endpoint that has always returned the nested data, so all of these lists
 * render identically without waiting on an API deploy.
 *
 * Falls through to whatever the bulk endpoint returned when the categories
 * request yields nothing (failed or still in flight), so the filter degrades to
 * the server-supplied list rather than going empty.
 */
export function useWithLeadSourceOptions(
  options: Record<string, FilterOption[]> | undefined,
  opts?: { enabled?: boolean },
): Record<string, FilterOption[]> {
  const leadSource = useLeadSourceOptions(opts);
  return useMemo(() => {
    const base = options ?? {};
    if (leadSource.length === 0) return base;
    return { ...base, 'lead-source': leadSource };
  }, [options, leadSource]);
}

/**
 * The canonical Lead Source filter config. Pages differ only in the label they
 * show it under ("Lead Source" on plans/leads/CRM, "Referral Source" on
 * patients) — never in behavior.
 *
 * Filter keys MUST match the canonical legacy filter registry; see
 * lib/filterRegistry.ts.
 */
export function leadSourceFilterConfig(
  label = 'Lead Source',
  allLabel = 'All lead sources',
): FilterConfig {
  return {
    key: 'lead-source',
    label,
    type: 'select',
    allLabel,
    multi: true,
    searchable: true,
    groupSelect: true,
    collapsibleGroups: true,
  };
}
